import UrlMixin from '@/components/query/url-mixin';
import { mixins } from 'vue-class-component';
import { Component, Watch } from 'vue-property-decorator';
import MainMenuComplexTabsMetaMixin from '@/views/layout/navigation/mixins/main-menu-complex-tabs-meta-mixin';
import { get } from 'lodash';

export interface ReportTab {
  key: string;
  icon?: string;
  title?: string;
  active?:  boolean;
  click?: (event?: any) => void;
  close?: (event?: any) => void;
  hideIfMany: boolean;
  hasBookmarks: boolean;
  link?: string;
  meta?: any;
}

@Component
export default class ReportTabsMixin extends mixins(UrlMixin, MainMenuComplexTabsMetaMixin) {
  get homeProps() {
    return {
      name: this.t('Home'),
      icon: 'fas fa-home'
    };
  }

  get dashboardProps() {
    return {
      name: this.t('Dashboard'),
      icon: 'fas fa-tachometer-alt-fast',
    };
  }

  get settingsProps() {
    return {
      name: this.t('Settings'),
      icon: 'fas fa-cog'
    };
  }

  get dashboardTab(): ReportTab[] {
    const result: ReportTab = {
      key: 'dashboard',
      title: this.dashboardProps.name,
      icon: this.dashboardProps.icon,
      active: this.isDashbord,
      click: () => this.clickTab(this.getDashboardLink(true)),
      close: null,
      hideIfMany: false,
      hasBookmarks: false,
      link: null
    };

    return [ result ];
  }

  get homeReportTab(): ReportTab[] {
    const active = !this.$store.state.table.activeTab && !this.isSettingsRoute && !this.isDashbord;
    const result: ReportTab = {
      key: 'home',
      title: this.homeProps.name,
      icon: this.homeProps.icon,
      active: active, 
      click: () => { 
        if(!active) {
          this.clickTab(this.getHomeLink());
        }
      },
      close: null,
      hideIfMany: false,
      hasBookmarks: false,
      link: null
    };

    return [ result ];
  }

  get settingsReportTabBase(): ReportTab {
    return {
      key: 'settings',
      title: this.settingsProps.name,
      icon: this.settingsProps.icon,
      active: this.isSettingsRoute,
      click: () => this.clickTab(this.$store.state.table.settingsTabRoute),
      close: () => this.closeSettingsTab(),
      hideIfMany: true,
      hasBookmarks: false,
      link: null
    };
  }

  get settingsReportTab(): ReportTab[] {
    return this.$store.state.table.settingsTabRoute
      ? [ this.settingsReportTabBase ] 
      : [];
  }

  get activeReports(): any[] {
    return this.$store.state.table.activeReports.filter((e: string) => e);
  }

  get commonReportTab(): ReportTab[] {
    return this.activeReports
      .map<ReportTab>((data, index) => ({
        key: data.name,
        index: index, 
        title: this.getTabName(data),
        icon: this.tabsMeta[data.filterTables] 
          ? this.tabsMeta[data.filterTables].icon 
          : data.icon,
        active: this.currentTab && this.currentTab.tabId === data.tabId,
        click: () => this.clickTab(data.url, data.tabId),
        close: (event) => this.closeTab(event, data.tabId),
        link: data.url,
        hideIfMany: true,
        hasBookmarks: true,
        meta: {
          tabId: data.tabId,
          mouseEvents: true,
          index: index,
          item: data,
        }
      } as ReportTab))

  }

  get reportTabs(): ReportTab[] {
    return this.dashboardTab
      .concat(this.homeReportTab)
      .concat(this.reportTabsClosable);
  }
  
  get reportTabsClosable() {
    return this.settingsReportTab
      .concat(this.commonReportTab);
  }

  lastActiveTab: any = {};

  @Watch('currentTab', { immediate: true })
  onCurrentTabChanged(value: any) {
    if(value) {
      this.lastActiveTab = this.commonReportTab.find((item: any) => value.tabId === item.meta.tabId)
    }
  }
  
  get currentCommonTabPropsNoHome() {
    const lastActiveTab = get(this.lastActiveTab, 'meta.item', {});

    return this.isSettingsRoute
      ? this.settingsProps
      : {
        name: this.getTabName(lastActiveTab),
        icon: this.tabsMeta[lastActiveTab.filterTables] 
          ? this.tabsMeta[lastActiveTab.filterTables].icon 
          : lastActiveTab.icon,
      }
  }

  get currentCommonTabProps() {
    return this.$store.state.table.activeTab || this.isSettingsRoute
      ? this.currentCommonTabPropsNoHome
      : this.isDashbord 
        ? this.dashboardProps
        : this.homeProps;
  }

  getBookmarkValues(): string[]  {
    return this.$store.state.bookmarks.map((item: any) => this.normalizeUrl(item.url));
  }

  get activeReportsUrls(): string[] {
    return this.activeReports.map((item) => {
      const url = item.tabId === this.$store.state.table.activeTab
        ? this.$route.fullPath
        : item.url;

      return this.normalizeUrl(url);
    });
  }

  get bookmarkIndexes()  {
    return this.activeReportsUrls.map((report) => {
      return this.getBookmarkValues().findIndex((bookmark) => report === bookmark);
    });
  }

  returnToLastPage() {
    const newTab = this.$store.state.table.activeReports.find((item: any) => item.tabId === this.$store.state.table.activeTab);
    const url = newTab 
      ? newTab.url
      : this.$store.state.table.activeTab === -1 && this.$store.state.table.settingsTabRoute
        ? this.$store.state.table.settingsTabRoute
        : this.getHomeLink();


    this.$router.push(url );
  }

  closeTabBase(tabId: number) {
    const { activeTab } = this.$store.state.table;
  
    this.$store.commit('tableCloseTab', tabId);
 
    if(activeTab === tabId) {
      this.returnToLastPage();
      return true;
    }

    return false;
  }

  closeTab(event: MouseEvent, tabId: number) {
    event.stopPropagation();
    return this.closeTabBase(tabId);
  }

  get currentTab() {
    return this.activeReports.find((item: any) => this.$store.state.table.activeTab === item.tabId);
  }

  getTabName(item: any) {
    return item.title || (item.drill && item.drill[0] && item.drill[0].name ) || this.t('Please select a tab');
  }

  get isSettingsRoute() {
    return this.$route.name.indexOf('app.profile') === 0;
  }



  closeSettingsTab() {
    this.$store.commit('tableSettingsTabRoute', null);

    this.$store.commit('tableCloseTab', -1);

    if(this.isSettingsRoute) {
      this.returnToLastPage();
      //this.clickTab(this.defaultHomepage());
    }
  }

  clickTab(url: any, tabId?: number) {
    if(this.$store.state.table.activeTab === tabId)
      return;

    if(tabId) {
      let tableActiveTabData: any = {
        tabId: tabId
      };

      if(this.$store.state.table.activeTab) {
        tableActiveTabData.url = this.$route.fullPath;
      }

      this.$store.commit('tableActiveTab', tableActiveTabData);
    }

    if(url === this.$route.fullPath) {
      this.bus.emit('reload-report');
    } else {
      this.$router.push(url, () => {});
    }
  }
}
